/**
 * CalendarPage — `/calendar`
 *
 * Hand-rolled month grid (no calendar library) showing exam badges on days
 * that have exams. GET /api/exams/calendar/?year=&month= (public).
 * Clicking a day with exams opens a panel listing that day's exams, which
 * link through to /exam/:code like the dashboard's UpcomingEvents widget.
 * Mirrors mobile/lib/features/calendar/presentation/calendar_screen.dart.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCalendarExams } from '@/api/social'
import { SectionHeader } from '@/components/kit'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, X } from 'lucide-react'

// Browser ICU data for the 'uz' locale is frequently incomplete (Intl falls
// back to terse forms like "M07" instead of "iyul"), so month/weekday names
// are hand-rolled here rather than sourced from Intl.DateTimeFormat — there's
// no date-formatting library in this project to lean on instead.
const MONTHS = {
  uz: ["Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun", "Iyul", "Avgust", "Sentabr", "Oktabr", "Noyabr", "Dekabr"],
  ru: ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'],
}
const WEEKDAYS = {
  uz: ['Du', 'Se', 'Cho', 'Pa', 'Ju', 'Sha', 'Ya'], // Mon..Sun
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
}

function startOfMonth(year, month) { return new Date(year, month - 1, 1) }
function daysInMonth(year, month) { return new Date(year, month, 0).getDate() }

export default function CalendarPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1) // 1-12
  const [exams, setExams] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    getCalendarExams(year, month, { signal: controller.signal })
      .then(({ data }) => setExams(data.exams ?? []))
      .catch((err) => { if (err.code !== 'ERR_CANCELED') setExams([]) })
      .finally(() => { if (!controller.signal.aborted) setLoading(false) })
    return () => controller.abort()
  }, [year, month])

  function shiftMonth(delta) {
    setSelectedDay(null)
    let m = month + delta
    let y = year
    if (m < 1) { m = 12; y -= 1 }
    if (m > 12) { m = 1; y += 1 }
    setMonth(m)
    setYear(y)
  }

  const examsByDay = useMemo(() => {
    const map = new Map()
    for (const ex of exams) {
      if (!ex.start_time) continue
      const day = new Date(ex.start_time).getDate()
      if (!map.has(day)) map.set(day, [])
      map.get(day).push(ex)
    }
    return map
  }, [exams])

  const lang = MONTHS[i18n.language] ? i18n.language : 'uz'

  const monthLabel = useMemo(() => {
    return `${MONTHS[lang][month - 1]} ${year}`
  }, [year, month, lang])

  const weekdayLabels = WEEKDAYS[lang]

  const firstWeekday = startOfMonth(year, month).getDay() // 0=Sun..6=Sat
  const leading = (firstWeekday + 6) % 7 // convert to Mon-first offset
  const totalDays = daysInMonth(year, month)
  const cells = leading + totalDays
  const rows = Math.ceil(cells / 7)

  const isToday = (day) =>
    year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate()

  const selectedExams = selectedDay ? (examsByDay.get(selectedDay) ?? []) : []

  return (
    <div>
      <SectionHeader
        title={t('calendar.title', 'Kalendar')}
        subtitle={t('calendar.subtitle', 'Imtihonlar jadvali')}
      />

      <div className="card" style={{ padding: 20, borderRadius: 22, marginBottom: 16 }}>
        {/* Month nav */}
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
          <button onClick={() => shiftMonth(-1)} className="btn btn-ghost" style={{ padding: 8 }} aria-label={t('calendar.prevMonth', 'Oldingi oy')}>
            <ChevronLeft size={18} />
          </button>
          <div className="h-display" style={{ fontSize: 17, textTransform: 'capitalize' }}>{monthLabel}</div>
          <button onClick={() => shiftMonth(1)} className="btn btn-ghost" style={{ padding: 8 }} aria-label={t('calendar.nextMonth', 'Keyingi oy')}>
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday header */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6, maxWidth: 480, margin: '0 auto 6px' }}>
          {weekdayLabels.map((w, i) => (
            <div key={i} className="t-xs t-mut fw-6" style={{ textAlign: 'center', textTransform: 'capitalize' }}>{w}</div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${rows}, 1fr)`, gap: 4, maxWidth: 480, margin: '0 auto' }}>
          {Array.from({ length: rows * 7 }).map((_, i) => {
            const day = i - leading + 1
            if (day < 1 || day > totalDays) return <div key={i} />
            const dayExams = examsByDay.get(day) ?? []
            const hasExam = dayExams.length > 0
            const today = isToday(day)
            const selected = selectedDay === day
            return (
              <button
                key={i}
                onClick={() => hasExam && setSelectedDay(day)}
                disabled={!hasExam}
                style={{
                  position: 'relative',
                  height: 44,
                  borderRadius: 12,
                  border: selected ? '2px solid var(--coral)' : '1px solid transparent',
                  background: today ? 'var(--coral)' : hasExam ? 'var(--coral-soft)' : 'transparent',
                  color: today ? '#fff' : hasExam ? 'var(--coral)' : 'var(--ink)',
                  fontWeight: hasExam || today ? 700 : 500,
                  fontSize: 13,
                  cursor: hasExam ? 'pointer' : 'default',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                }}
              >
                {day}
                {hasExam && (
                  <span style={{
                    width: 4, height: 4, borderRadius: '50%',
                    background: today ? '#fff' : 'var(--coral)',
                  }} />
                )}
                {dayExams.length > 1 && (
                  <span
                    className="badge"
                    style={{
                      position: 'absolute', top: -6, right: -4, padding: '1px 5px', fontSize: 9,
                      background: 'var(--danger)', color: '#fff', border: '1.5px solid var(--surface)',
                    }}
                  >
                    +{dayExams.length - 1}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {loading && (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'var(--coral-soft)', borderTopColor: 'var(--coral)' }} />
          </div>
        )}
      </div>

      {/* Upcoming exams this month */}
      <div className="card" style={{ padding: 8, borderRadius: 22 }}>
        <div style={{ padding: '12px 16px' }}>
          <div className="h-display dot" style={{ fontSize: 18 }}>{t('calendar.examsThisMonth', 'Bu oydagi imtihonlar')}</div>
        </div>
        {!loading && exams.length === 0 && (
          <div className="t-sm t-mut" style={{ padding: '14px 16px' }}>{t('calendar.noExamsMonth', "Bu oyda imtihonlar yo'q")}</div>
        )}
        {exams.map((ex, i) => (
          <ExamRow key={ex.id} exam={ex} bordered={i > 0} onClick={() => navigate(`/exam/${ex.code}`)} />
        ))}
      </div>

      {/* Day detail panel */}
      {selectedDay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.35)' }}
          onClick={() => setSelectedDay(null)}
        >
          <div
            className="card"
            style={{ padding: 8, borderRadius: 22, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="row" style={{ justifyContent: 'space-between', padding: '12px 16px' }}>
              <div className="h-display" style={{ fontSize: 16 }}>
                {selectedDay} {monthLabel} · {t('calendar.dayExams', "Kunlik imtihonlar")}
              </div>
              <button onClick={() => setSelectedDay(null)} className="btn btn-ghost" style={{ padding: 6 }}>
                <X size={16} />
              </button>
            </div>
            {selectedExams.length === 0 ? (
              <div className="t-sm t-mut" style={{ padding: '14px 16px' }}>{t('calendar.noExamsDay', "Bu kunda imtihonlar yo'q")}</div>
            ) : (
              selectedExams.map((ex, i) => (
                <ExamRow key={ex.id} exam={ex} bordered={i > 0} onClick={() => navigate(`/exam/${ex.code}`)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ExamRow({ exam, bordered, onClick }) {
  const dt = exam.start_time ? new Date(exam.start_time) : null
  return (
    <button
      onClick={onClick}
      className="row gap-16"
      style={{
        width: '100%', textAlign: 'left', background: 'transparent', border: 0, cursor: 'pointer',
        padding: '14px 16px', borderTop: bordered ? '1px solid var(--border)' : 0, font: 'inherit',
      }}
    >
      <span
        className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0 border"
        style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)', color: 'var(--coral)' }}
      >
        <CalendarIcon size={16} strokeWidth={2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fw-7 t-sm truncate">{exam.title}</div>
        <div className="row gap-10" style={{ marginTop: 3 }}>
          {dt && (
            <span className="t-xs t-mut row gap-4">
              <Clock size={11} /> {dt.toLocaleDateString()} · {dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {exam.teacher_name && (
            <span className="t-xs t-mut row gap-4">
              <User size={11} /> {exam.teacher_name}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}
